<template>
  <div v-if="nf" class="nutrition-facts">
    <div class="nutrition-facts__title">{{ $t('nutritionFacts') }}</div>

    <!-- Serving Information -->
    <div v-if="nf.servingsPerContainer" class="nutrition-facts__serving">
      {{ nf.servingsPerContainer }} {{ $t('servingsPerContainer') }}
    </div>
    <div v-if="nf.servingSize" class="nutrition-facts__serving--size">
      <span>{{ $t('servingSize') }}</span>
      <span class="float-right">{{ nf.servingSize }}</span>
    </div>

    <div class="nutrition-facts__bar--large"></div>

    <!-- Calorie Information -->
    <template v-if="nf.calories">
      <span class="nutrition-facts__amount-label">{{ $t('amountPerServing') }}</span>
      <div class="nutrition-facts__calorie-wrapper">
        <span class="nutrition-facts__calorie-label">{{ $t('calories') }}</span>
        <span class="nutrition-facts__calorie-value">{{ nf.calories }}</span>
      </div>
      <div class="nutrition-facts__bar--medium"></div>
    </template>

    <div class="nutrition-facts__line__daily-value text-right">{{ $t('percentDailyValue') }}</div>

    <!-- Primary Nutrition Facts -->
    <div
      class="nutrition-facts__line"
      v-for="fact in nf.facts"
      :key="fact.id"
      :class="{ 'nutrition-facts__line--child': fact.isChild }"
      :style="{ 'padding-left.em': fact.depth }"
    >
      <template v-if="fact.id !== 52">
        <b v-if="!fact.isChild">{{ fact.name }}</b>
        <span v-if="fact.isChild">{{ fact.name }}</span>
        <span v-if="fact.value !== null"> {{ fact.value }}{{ fact.unit }}</span>
        <div v-if="fact.percent !== null" class="nutrition-facts__line__percent">
          {{ fact.percent }}%
        </div>
      </template>
      <template v-if="fact.id === 52">
        <span>
          {{ $t('includes') }}<span v-if="fact.value !== null">{{ fact.value }}{{ fact.unit }}</span
          >{{ $t('addedSugars') }}
        </span>
        <div v-if="fact.percent !== null" class="nutrition-facts__line__percent">
          {{ fact.percent }}%
        </div>
      </template>
    </div>

    <div class="nutrition-facts__bar--large"></div>

    <!-- Nutrient Items -->
    <div
      class="nutrition-facts__line nutrition-facts__line--nutrient"
      v-for="fact in nf.nutrients"
      :key="fact.id"
    >
      <span>{{ fact.name }}</span>
      <span v-if="fact.value !== null"> {{ fact.value }}{{ fact.unit }}</span>
      <div
        v-if="fact.percent !== null"
        class="nutrition-facts__line__percent nutrition-facts__line__percent--nutrient"
      >
        {{ fact.percent }}%
      </div>
    </div>

    <div class="nutrition-facts__bar--medium"></div>

    <div class="nutrition-facts__footer">
      <div class="nutrition-facts__disclaimer">
        <span class="nutrition-facts__disclaimer__star">*</span> {{ $t('percentDV') }}
      </div>
      <div v-if="nf.ingredients" class="nutrition-facts__ingredients">
        <b>{{ $t('ingredientsCap') }}:</b> {{ nf.ingredients }}
      </div>
    </div>
  </div>
</template>

<script>
const CALORIES_LABEL = 'Calories';
const CALORIES_FROM_FAT_LABEL = 'Calories From Fat';
const CALORIES_FROM_SATURATED_FAT_LABEL = 'Calories From Saturated Fat';
const CALORIES_FROM_SATURATED_AND_TRANS_FAT_LABEL = 'Calories From Saturated + Trans';

const STATIC_NUTRTION_FACT_ITEMS = [
  CALORIES_LABEL,
  CALORIES_FROM_FAT_LABEL,
  CALORIES_FROM_SATURATED_FAT_LABEL,
  CALORIES_FROM_SATURATED_AND_TRANS_FAT_LABEL,
];

export default {
  props: {
    item: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      nf: null,
    };
  },
  created() {
    this.CALORIES_LABEL = CALORIES_LABEL;
    this.STATIC_NUTRTION_FACT_ITEMS = STATIC_NUTRTION_FACT_ITEMS;

    this.nf = this.buildNutritionFacts(this.item);
  },
  methods: {
    buildNutritionFacts(item) {
      if (item && item.nutrition) {
        const facts = [];

        item.nutrition.forEach(el => {
          const isNutrient = el.order <= 0;
          if (!STATIC_NUTRTION_FACT_ITEMS.includes(el.name) && !isNutrient) {
            const value = el.value || el.value === 0 ? el.value : null;
            const percent = el.percent || el.percent === 0 ? el.percent : null;
            facts.push({
              id: el.id,
              name: el.name,
              value,
              percent,
              unit: el.unit,
              isChild: el.parent > 0,
              depth: el.depth,
            });
          }
        });

        const nutrients = [];
        item.nutrition.forEach(el => {
          const isNutrient = el.order <= 0;
          if (!STATIC_NUTRTION_FACT_ITEMS.includes(el.name) && isNutrient) {
            const value = el.value || el.value === 0 ? el.value : null;
            const percent = el.percent || el.percent === 0 ? el.percent : null;

            nutrients.push({
              id: el.id,
              name: el.name,
              value,
              percent,
              unit: el.unit,
              depth: el.depth,
            });
          }
        });

        const calories = item.nutrition.find(el => el.name === CALORIES_LABEL);
        return {
          servingSize: item.servingSize,
          servingsPerContainer: item.servingsPerContainer,
          calories: calories && (calories.value || calories.value === 0) ? calories.value : null,
          facts,
          nutrients,
          ingredients: item.ingredients,
        };
      }

      return null;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/tools';
@import '~@/assets/styles/settings';

// Extra small devices
@include media-breakpoint-down(sm) {
  .nutrition-facts {
    margin: 0 auto;
  }
}

.nutrition-facts {
  font-size: $font-size-xl;
  margin-bottom: $margin-3;
  padding: $pad-1;
  font-family: Arial, Helvetica, sans-serif;
  color: #333;
  border: 0.05rem solid #333;
  width: min-content;
}

.nutrition-facts__title {
  font-size: $font-size-4xl;
  font-weight: bold;
  line-height: 1;
  white-space: nowrap;
  border-bottom: 0.031rem solid #333;
}

.nutrition-facts__serving {
  font-size: $font-size-xl;
}

.nutrition-facts__serving--size {
  font-weight: bold;
  font-size: $font-size-xl;
}

.nutrition-facts__amount-label {
  font-weight: bold;
  font-size: $font-size-base * 1.093;
}

.nutrition-facts__calorie-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  line-height: 1;
  padding-bottom: $pad-3 * 0.3125;
}

.nutrition-facts__calorie-label {
  font-weight: bold;
  font-size: $font-size-3xl;
}

.nutrition-facts__calorie-value {
  font-weight: bold;
  font-size: $font-size-base * 2.5;
}

.nutrition-facts__line__daily-value {
  font-weight: bold;
  font-size: $font-size-base * 0.781;
}

.nutrition-facts__bar--large {
  height: 0.875rem;
  background: #333;
}
.nutrition-facts__bar--medium {
  height: 0.375rem;
  background: #333;
}

.nutrition-facts__line--medium {
  height: 0.375rem;
  background-color: #333;
}

.nutrition-facts__line {
  font-weight: bold;
  font-size: $font-size-base * 1.093;
  line-height: 1.4;
  padding: $pad-3 * 0.3125 0;
  border-top: 0.031rem solid #333;
  vertical-align: middle;
}

.nutrition-facts__line--child {
  font-weight: normal;
}

.nutrition-facts__line--last {
  border-bottom: 0.031rem solid #333;
}

.nutrition-facts__line--nutrient {
  font-weight: normal;
}

.nutrition-facts__line__percent {
  float: right;
  font-weight: bold;
}

.nutrition-facts__line__percent--nutrient {
  font-weight: normal;
}

.nutrition-facts__disclaimer {
  margin-top: $margin-3 * 1.25;
  font-size: $font-size-base * 0.781;
  line-height: 1.2;
  padding-left: $pad-3 * 0.625;
}

.nutrition-facts__disclaimer__star {
  margin-left: $margin-n3 * 0.625;
}

.nutrition-facts__ingredients {
  margin-top: $margin-3 * 1.25;
  font-size: $font-size-base * 0.781;
  line-height: 1.2;
}
</style>
